import { reactive } from "vue";
import { SIZES } from "../ts/types/Sizes.js";
import { cn } from "@/lib/utils";
export var EStates = /* @__PURE__ */ ((EStates2) => {
  EStates2["DEFAULT"] = "default";
  EStates2["HOVER"] = "hover";
  EStates2["PRESSED"] = "pressed";
  EStates2["FOCUSED"] = "focused";
  EStates2["DISABLED"] = "disabled";
  EStates2["CHECKED"] = "checked";
  EStates2["UNCHECKED"] = "unchecked";
  EStates2["SELECTED"] = "selected";
  EStates2["UNSELECTED"] = "unselected";
  EStates2["INDETERMINATE"] = "indeterminate";
  EStates2["SUCCESS"] = "success";
  EStates2["WARNING"] = "warning";
  EStates2["ERROR"] = "error";
  EStates2["READ_ONLY"] = "read_only";
  EStates2["CLICKED"] = "clicked";
  EStates2["LOADING"] = "loading";
  return EStates2;
})(EStates || {});
export var EVariants = /* @__PURE__ */ ((EVariants2) => {
  EVariants2["DEFAULT"] = "default";
  EVariants2["PRIMARY"] = "primary";
  EVariants2["SECONDARY"] = "secondary";
  EVariants2["TERTIARY"] = "tertiary";
  EVariants2["ACCENT"] = "accent";
  EVariants2["PLAIN"] = "plain";
  EVariants2["HIGHLIGHT"] = "highlight";
  EVariants2["SUCCESS"] = "success";
  EVariants2["WARNING"] = "warning";
  EVariants2["ERROR"] = "error";
  EVariants2["LIGHT"] = "light";
  EVariants2["DARK"] = "dark";
  EVariants2["CAROUSEL"] = "carousel";
  return EVariants2;
})(EVariants || {});
export default function useCreateComponentConfig() {
  const config = reactive({
    elements: {}
  });
  const addElement = (elementName) => {
    if (!config.elements[elementName]) {
      config.elements[elementName] = {
        base: { base: "", states: {} },
        variants: {},
        sizes: {},
        default: {
          variant: "default" /* DEFAULT */,
          size: SIZES.MEDIUM
        }
      };
    }
  };
  const setBase = (elementName, baseClasses) => {
    addElement(elementName);
    const currentBase = config.elements[elementName].base.base;
    config.elements[elementName].base.base = cn(currentBase, baseClasses);
  };
  const addBaseState = (elementName, state, classes, callback) => {
    addElement(elementName);
    config.elements[elementName].base.states[state] = classes;
    if (callback && callback.condition) {
      callback.func(elementName, state, classes);
    }
  };
  const overwriteBaseState = (elementName, stateToBeOverwritten, classes) => {
    config.elements[elementName].base.states[stateToBeOverwritten] = classes;
  };
  const addVariant = (elementName, variant, variantConfig) => {
    if (Object.keys(variantConfig.states || {}).length > 0 && variantConfig.extendStates) {
      console.warn(
        `Variant "${variant}" for element "${elementName}" cannot use both "states" and "extendStates" at the same time. Please choose one.`
      );
    }
    addElement(elementName);
    config.elements[elementName].variants[variant] = variantConfig;
  };
  const addSize = (elementName, size, classes) => {
    addElement(elementName);
    config.elements[elementName].sizes[size] = classes;
  };
  const syncElementStates = (sourceElement, targetElement, states) => {
    states.forEach((state) => {
      addBaseState(sourceElement, state, config.elements[sourceElement].base.states[state] || "", {
        func: () => {
          const sourceState = config.elements[sourceElement].base.states[state];
          overwriteBaseState(targetElement, state, sourceState);
        },
        condition: true
      });
    });
  };
  const addOrUpdateVariantState = (elementName, variant, state, classes) => {
    addElement(elementName);
    config.elements[elementName].variants[variant] = config.elements[elementName].variants[variant] ?? {
      base: "",
      states: {}
    };
    config.elements[elementName].variants[variant].states[state] = classes;
  };
  const setDefault = (elementName, variant, size) => {
    addElement(elementName);
    config.elements[elementName].default = { variant, size };
  };
  const DEFAULT_STATES = ["default" /* DEFAULT */, "hover" /* HOVER */, "focused" /* FOCUSED */, "pressed" /* PRESSED */];
  const getClasses = (elementName, variant, size, state, disabled) => {
    const element = config.elements[elementName];
    if (!element) {
      return "";
    }
    const usedVariant = variant || element.default.variant;
    const usedSize = size || element.default.size;
    const variantConfig = element.variants[usedVariant];
    if (variantConfig?.extendStates) {
      variantConfig.states = variantConfig.extendStates;
    }
    const baseClasses = [element.base.base, element.sizes[usedSize] || ""];
    const variantBaseClasses = variantConfig ? [variantConfig.base || ""] : [];
    const getDefaultStateClasses = (config2) => DEFAULT_STATES.map((s) => config2.states[s] || "").filter(Boolean);
    const baseDefaultStateClasses = getDefaultStateClasses(element.base);
    const isDisabled = disabled === true || state === "disabled" /* DISABLED */;
    if (isDisabled) {
      return cn(
        ...baseClasses,
        ...baseDefaultStateClasses,
        element.base.states["disabled" /* DISABLED */] || "",
        ...variantConfig?.extendStates ? variantBaseClasses : [],
        variantConfig ? variantConfig.states["disabled" /* DISABLED */] || "" : ""
      );
    }
    if (variant && (!state || state === "default" /* DEFAULT */)) {
      const variantDefaultStateClasses = variantConfig ? getDefaultStateClasses(variantConfig) : [];
      return cn(
        ...baseClasses,
        element.base.states[state || "default" /* DEFAULT */] || "",
        ...baseDefaultStateClasses,
        ...variantBaseClasses,
        ...variantDefaultStateClasses
      );
    }
    if (state && state !== "default" /* DEFAULT */) {
      return cn(
        ...baseClasses,
        ...baseDefaultStateClasses,
        element.base.states[state] || "",
        ...variantConfig?.extendStates ? variantBaseClasses : [],
        variantConfig ? variantConfig.states[state] || "" : ""
      );
    }
    return cn(...baseClasses, ...baseDefaultStateClasses);
  };
  return {
    config,
    setBase,
    addBaseState,
    addVariant,
    addSize,
    addOrUpdateVariantState,
    setDefault,
    getClasses,
    syncElementStates,
    overwriteBaseState
  };
}
